export type Board = { title: string; size: 3 | 4 | 5; cells: string[]; free: boolean; shuffle: boolean };

/** A laid-out cell: text, `''` = not filled in yet, `null` = the free centre. */
export type Cell = string | null;

export type Saved = { data: string; title: string; at: number };

const MAX_CELLS = 100;
const MAX_LEN = 140;
const MAX_TITLE = 80;
const MINE = 'bingo:mine';

export function hasFree(b: Board): boolean {
  return b.free && b.size % 2 === 1;
}

export function needed(b: Board): number {
  return b.size * b.size - (hasFree(b) ? 1 : 0);
}

export function cleanCells(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_CELLS)
    .map((s) => s.slice(0, MAX_LEN));
}

function toB64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeBoard(b: Board): string {
  return toB64url(
    JSON.stringify({ v: 1, t: b.title, s: b.size, c: b.cells, f: b.free ? 1 : 0, r: b.shuffle ? 1 : 0 })
  );
}

export function decodeBoard(data: string): Board | null {
  try {
    const p = JSON.parse(fromB64url(decodeURIComponent(data)));
    if (!p || typeof p !== 'object') return null;
    if (p.s !== 3 && p.s !== 4 && p.s !== 5) return null;
    if (!Array.isArray(p.c)) return null;
    const cells = (p.c as unknown[])
      .filter((x): x is string => typeof x === 'string')
      .map((x) => x.trim().slice(0, MAX_LEN))
      .filter(Boolean)
      .slice(0, MAX_CELLS);
    return {
      title: typeof p.t === 'string' ? p.t.slice(0, MAX_TITLE) : '',
      size: p.s,
      cells,
      free: !!p.f,
      shuffle: !!p.r,
    };
  } catch {
    return null;
  }
}

export function hashString(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function newSeed(): number {
  return 1 + Math.floor(Math.random() * 2147483646);
}

/** Seed 0 keeps the author's order; any other seed shuffles when the board allows it. */
export function layout(b: Board, seed: number): Cell[] {
  const pool: Cell[] = [...b.cells];
  if (b.shuffle && seed !== 0) {
    const rnd = mulberry32(seed);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
  }
  const need = needed(b);
  const cells = pool.slice(0, need);
  while (cells.length < need) cells.push('');
  if (hasFree(b)) cells.splice(Math.floor((b.size * b.size) / 2), 0, null);
  return cells;
}

export function winning(size: number, marked: boolean[]) {
  const lines: number[][] = [];
  for (let r = 0; r < size; r++) lines.push(Array.from({ length: size }, (_, c) => r * size + c));
  for (let c = 0; c < size; c++) lines.push(Array.from({ length: size }, (_, r) => r * size + c));
  lines.push(Array.from({ length: size }, (_, i) => i * size + i));
  lines.push(Array.from({ length: size }, (_, i) => i * size + (size - 1 - i)));
  const cells = new Set<number>();
  let count = 0;
  for (const line of lines) {
    if (line.every((i) => marked[i])) {
      count++;
      line.forEach((i) => cells.add(i));
    }
  }
  return { count, cells, full: marked.length > 0 && marked.every(Boolean) };
}

export function load<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

export function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the page still works, it just forgets */
  }
}

export function forget(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export function loadMine(): Saved[] {
  const v = load<unknown>(MINE, []);
  if (!Array.isArray(v)) return [];
  return v.filter((x) => x && typeof x.data === 'string' && typeof x.title === 'string') as Saved[];
}

export function addMine(data: string, title: string): void {
  const list = loadMine().filter((m) => m.data !== data);
  list.unshift({ data, title, at: Date.now() });
  save(MINE, list.slice(0, 30));
}

export function removeMine(data: string): Saved[] {
  const list = loadMine().filter((m) => m.data !== data);
  save(MINE, list);
  return list;
}
